/**
 * Vuexストアの状態管理とアクションを提供するモジュール
 * @module vuex
 */
import { useStore } from "vuex";

/**
 * Vue Routerのルーティング機能を提供するモジュール
 * @module vue-router
 */
import { useRoute } from "vue-router";

/**
 * ルーティング関連のユーティリティ関数を提供するモジュール
 * @module router.util
 */
import RouterUtils from "@/common/utils/router.util";

/**
 * ルーティング定数を定義するモジュール
 * @module router.constants
 */
import RouterConstants from "../constants/router.constants";

/**
 * 予約フローの各ステップでのバリデーションを管理する関数
 *
 * 予約フローの各画面で必要な情報が入力されているかを確認し、
 * 不足している情報がある場合は適切な画面にリダイレクトします。
 *
 * バリデーションの順序:
 * 1. メニューカテゴリーの選択確認
 * 2. メニューの選択確認
 * 3. オプションの選択確認
 * 4. スタッフの選択確認（任意）
 * 5. 予約時間の選択確認
 * 6. 顧客情報の入力確認
 *
 * @returns {void}
 */
const ValidateBookingSteps = () => {
  const store = useStore();
  const route = useRoute();

  /**
   * ルートごとに「そこへ到達する前に満たすべき条件」を述語関数の配列で定義
   * - **キー** : ルート名
   * - **値** : `true` でなければならない条件を返すコールバックの配列
   *
   * @type {Record<string, (() => boolean)[]>}
   */
  const routeGuards: Record<string, (() => boolean)[]> = {
    // カテゴリー画面
    [RouterConstants.BOOKING_MENU_CATEGORY_ROUTER_NAME]: [],

    // メニュー画面
    [RouterConstants.BOOKING_MENU_ROUTER_NAME]: [],

    // オプション選択画面
    [RouterConstants.BOOKING_OPTION_ROUTER_NAME]: [() => store.getters["booking/selectedMenu"].id != 0],

    // スタッフ選択画面
    [RouterConstants.BOOKING_ROUTER_NAME]: [() => store.getters["booking/selectedMenu"].id != 0],

    // カレンダー画面
    [RouterConstants.BOOKING_CALENDAR_ROUTER_NAME]: [
      () => store.getters["booking/selectedStaffId"] != 0,
      () => store.getters["booking/selectedMenu"].id != 0,
    ],

    // 個人情報画面
    [RouterConstants.PERSONAL_INFO_ROUTER_NAME]: [
      () => {
        const selectedMenu = store.getters["booking/selectedMenu"];
        if (selectedMenu?.bookingType === "request") {
          // リクエスト予約の場合は、selectedRequestDatesが存在し、少なくとも1つの日時が設定されていることを確認
          const selectedRequestDates = store.getters["booking/selectedRequestDates"];
          return (
            selectedRequestDates &&
            (selectedRequestDates.first || selectedRequestDates.second || selectedRequestDates.third)
          );
        } else {
          // 通常予約の場合は、selectedTimeが存在することを確認
          return Object.keys(store.getters["booking/selectedTime"]).length > 0;
        }
      },
    ],

    // 内容確認画面
    [RouterConstants.CONTENT_CONFIRMATION_ROUTER_NAME]: [
      () => {
        const isChildBooking = store.getters["booking/isChildBooking"];
        if (isChildBooking) {
          // 子ども予約の場合：親情報と子ども情報をチェック
          const parentInfo = store.getters["booking/parentInfo"];
          const childInfo = store.getters["booking/childInfo"];
          return childInfo?.lastName !== "" && parentInfo?.lastName !== "" && parentInfo?.tel !== "";
        } else {
          // 通常予約の場合：既存のチェック
          return (
            store.getters["booking/customerInfo"].familyName !== "" && store.getters["booking/customerInfo"].tel !== ""
          );
        }
      },
    ],
  };

  // 現在ルートに定義が無ければバリデーション不要
  const guards = routeGuards[route.name as string];
  if (!guards || guards.length === 0) return; // チェック不要画面なら終了

  // すべてのガードを満たしていればそのまま続行
  if (guards.every(fn => fn())) return;

  /**
   * ガードを満たさなかった場合に遷移させるフォールバック先
   * - 最低限「一歩前」の画面へ戻す
   * - 定義が無ければ最初の画面へ
   *
   * @type {Record<string, string>}
   */
  const fallbackMap: Record<string, string> = {
    [RouterConstants.BOOKING_MENU_ROUTER_NAME]: RouterConstants.BOOKING_MENU_CATEGORY_ROUTER_NAME,
    [RouterConstants.BOOKING_OPTION_ROUTER_NAME]: RouterConstants.BOOKING_MENU_ROUTER_NAME,
    [RouterConstants.BOOKING_ROUTER_NAME]: RouterConstants.BOOKING_OPTION_ROUTER_NAME,
    [RouterConstants.BOOKING_CALENDAR_ROUTER_NAME]: RouterConstants.BOOKING_ROUTER_NAME,
    [RouterConstants.PERSONAL_INFO_ROUTER_NAME]: RouterConstants.BOOKING_CALENDAR_ROUTER_NAME,
    [RouterConstants.CONTENT_CONFIRMATION_ROUTER_NAME]: RouterConstants.PERSONAL_INFO_ROUTER_NAME,
  };

  RouterUtils.goTo(fallbackMap[route.name as string] || RouterConstants.BOOKING_ROUTER_NAME);
};

export default ValidateBookingSteps;
